import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MedSummaryEntryEditActions } from '../store/entries-edit.slice'
import { RootState } from '@/store/store'
import { useMedSummaryParent } from '@/medical-billing-summaries/parent-context'
import { EditableEntryField } from '../editable-field-config'

interface UseStartEditFieldParams {
  entryId: string
  fieldKey: EditableEntryField
  originalValue: string
}

/**
 * Use Start Edit Field
 *
 * Returns a function that can be called to enter edit mode for the specified field.
 * The billing-summary asset comes from context.
 *
 * @param entryId - The med summary entry ID
 * @param fieldKey - The med summary field key
 * @param originalValue - The original value of the field
 * @returns A function to start editing the field
 */
export function useStartEditField(params: UseStartEditFieldParams) {
  const { entryId, fieldKey, originalValue } = params
  const dispatch = useAppDispatch()
  const { id: parentId, readOnly } = useMedSummaryParent()

  const isAnyFieldEditing = useAppSelector((state: RootState) => state.medSummaryEntryEditState.activeEdit !== null)

  const startEdit = useCallback(() => {
    if (readOnly) return // Collaborators can read the summary but not edit it
    if (!parentId || !entryId || !fieldKey) return // Required fields to start an edit
    if (isAnyFieldEditing) return // Only one field can be edited at a time

    // When editing the amount field, remove the thousands separators and negative sign from the original value to match the mask
    let formattedOriginalValue = originalValue
    if (fieldKey === EditableEntryField.AMOUNT) {
      formattedOriginalValue = originalValue.replace(/[,-]/g, '')
    }

    dispatch(MedSummaryEntryEditActions.startEditField({ parentId, entryId, fieldKey, originalValue: formattedOriginalValue }))
  }, [dispatch, parentId, readOnly, entryId, fieldKey, originalValue, isAnyFieldEditing])

  return startEdit
}
